<script setup lang="ts">
  import { useDialogA11y } from '@/composables/useDialogA11y'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed } from 'vue'

  const props = withDefaults(
    defineProps<{
      open: boolean
      accountEmail: string
      affectedLeistungen: string[]
      loading?: boolean
      loadFailed?: boolean
      disconnecting?: boolean
    }>(),
    {
      loading: false,
      loadFailed: false,
      disconnecting: false
    }
  )

  const emit = defineEmits<{
    close: []
    confirm: []
  }>()

  const requestClose = (): void => {
    emit('close')
  }

  const { dialogRef, onDialogKeydown } = useDialogA11y(
    computed(() => props.open),
    requestClose
  )

  const { t } = useTranslation({
    de: {
      disconnectConfirmTitle: 'Konto wirklich trennen?',
      disconnectConfirmDescription: '{email} wird aus Kalender und Terminbuchung entfernt.',
      affectedServicesTitle: 'Achtung: Buchbare Leistungen betroffen',
      affectedServicesDescription:
        'Folgende Leistungen werden nach dem Trennen nicht mehr buchbar sein:',
      affectedServicesClearTitle: 'Keine aktiven Leistungen betroffen',
      affectedServicesEmpty: 'Keine aktiven Leistungen sind von diesem Trennen betroffen.',
      affectedServicesLoading: 'Betroffene Leistungen werden geladen …',
      affectedServicesLoadError:
        'Die betroffenen Leistungen konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.',
      cancel: 'Abbrechen',
      disconnect: 'Trennen',
      disconnecting: 'Trennen ...'
    },
    en: {
      disconnectConfirmTitle: 'Really disconnect account?',
      disconnectConfirmDescription:
        '{email} will be removed from calendar and appointment booking.',
      affectedServicesTitle: 'Warning: bookable services affected',
      affectedServicesDescription:
        'The following services will no longer be bookable after disconnecting:',
      affectedServicesClearTitle: 'No active services affected',
      affectedServicesEmpty: 'No active services are affected by this disconnect.',
      affectedServicesLoading: 'Loading affected services …',
      affectedServicesLoadError:
        'The affected services could not be loaded right now. Please try again.',
      cancel: 'Cancel',
      disconnect: 'Disconnect',
      disconnecting: 'Disconnecting ...'
    }
  })
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.open"
      class="fixed inset-0 z-[90] flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-[1px]"
      @click.self="requestClose"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disconnect-warning-title"
        aria-describedby="disconnect-warning-description"
        tabindex="-1"
        class="calendar-main border-layer-line bg-layer w-full max-w-md rounded-xl border p-5 shadow-2xl"
        @keydown="onDialogKeydown"
      >
        <h3
          id="disconnect-warning-title"
          class="text-base font-semibold text-foreground"
        >
          {{ t('disconnectConfirmTitle') }}
        </h3>
        <p
          id="disconnect-warning-description"
          class="text-muted-foreground-1 mt-1 text-xs"
        >
          {{ t('disconnectConfirmDescription', { email: props.accountEmail }) }}
        </p>

        <div
          class="mt-3 rounded-md p-3 text-xs text-foreground"
          :class="
            props.loadFailed || props.affectedLeistungen.length > 0
              ? 'border border-rose-500/30 bg-rose-500/10'
              : 'border-layer-line bg-surface border'
          "
        >
          <p
            class="font-medium"
            :class="
              props.loadFailed || props.affectedLeistungen.length > 0
                ? 'text-rose-500'
                : 'text-foreground'
            "
          >
            {{
              props.affectedLeistungen.length > 0 || props.loadFailed
                ? t('affectedServicesTitle')
                : t('affectedServicesClearTitle')
            }}
          </p>
          <p
            v-if="props.loading"
            class="mt-1"
            data-cy="disconnect-warning-loading"
          >
            {{ t('affectedServicesLoading') }}
          </p>
          <p
            v-else-if="props.loadFailed"
            class="mt-1"
            data-cy="disconnect-warning-error"
          >
            {{ t('affectedServicesLoadError') }}
          </p>
          <template v-else>
            <p
              v-if="props.affectedLeistungen.length > 0"
              class="mt-1"
            >
              {{ t('affectedServicesDescription') }}
            </p>
            <ul
              v-if="props.affectedLeistungen.length > 0"
              class="mt-2 space-y-1"
              data-cy="disconnect-warning-list"
            >
              <li
                v-for="leistung in props.affectedLeistungen"
                :key="leistung"
                class="font-medium"
              >
                {{ leistung }}
              </li>
            </ul>
            <p
              v-else
              class="mt-2"
              data-cy="disconnect-warning-empty"
            >
              {{ t('affectedServicesEmpty') }}
            </p>
          </template>
        </div>

        <div class="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            class="text-muted-foreground-2 hover:bg-muted-hover focus:outline-hidden rounded-lg px-3 py-2 text-sm"
            @click="requestClose"
          >
            {{ t('cancel') }}
          </button>
          <button
            type="button"
            class="focus:outline-hidden rounded-lg bg-rose-500 px-3 py-2 text-sm font-medium text-white
              hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="props.loading || props.loadFailed || props.disconnecting"
            data-cy="disconnect-warning-confirm"
            @click="emit('confirm')"
          >
            {{ props.disconnecting ? t('disconnecting') : t('disconnect') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
